import { useFpCampaignPaymentService } from '@cart-services/hooks/useFpCampaignPaymentService'
import { CreditCardOrganizationIcons } from '@cart-ui/CreditCardOrganizationIcons/CreditCardOrganizationIcons'
import { RadioPayment } from '@cart-ui/RadioPayment/RadioPayment'
import { type PaymentMethod, PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { CARD_SOURCE, ECPayCreditCardFields, TappayCreditCardFields } from '@payments'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { ApplePay, JkoPay, LinePay } from '@pets-ui/Icons'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption, type SelectOptionValue } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { type ChangeEvent, type ReactNode, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextCheckout } from '../CheckoutProvider/useContextCheckout'

const IconMap: Partial<Record<PaymentMethodType, ReactNode>> = {
  [PaymentMethodType.LINE_PAY]: <LinePay />,
  [PaymentMethodType.JKO_PAY]: <JkoPay />,
  [PaymentMethodType.APPLE_PAY]: <ApplePay />,
}

const PaymentMethodTitleMap: Partial<Record<PaymentMethodType, ReactNode>> = {
  [PaymentMethodType.CREDIT_CARD_ONCE]: '信用卡 一次付清',
  [PaymentMethodType.CREDIT_CARD_INSTALLMENT]: '信用卡 分期付款',
  [PaymentMethodType.CASH_ON_DELIVERY]: '貨到付款',
  [PaymentMethodType.CVS]: '超商代碼繳費',
  [PaymentMethodType.ATM]: 'ATM 轉帳',
  [PaymentMethodType.ASSIGNED_PAYMENT]: '指定付款',
  [PaymentMethodType.ASSIGNED_PAYMENT_INVOICE]: '指定付款（開發票）',
  [PaymentMethodType.APPLE_PAY]: 'Apple Pay',
  [PaymentMethodType.LINE_PAY]: 'LINE Pay',
  [PaymentMethodType.JKO_PAY]: 'JKO Pay',
  [PaymentMethodType.OFFLINE_PAYMENT]: (
    <HStack alignItems="center">
      <Text fontWeight={600}>現場付款</Text>
      <Text fontWeight={600} color={colors.red600}>（需排隊）</Text>
    </HStack>
  ),
}

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  selectWrapper: {
    maxWidth: {
      [MOBILE]: '100%',
      default: 264,
    },
  },
  creditCardBox: {
    padding: 12,
    borderRadius: 4,
    backgroundColor: colors.grey100,
  },
  radioPaymentChecked: {
    backgroundColor: colors.white,
  },
})

export interface PaymentListItemProps {
  paymentMethods: PaymentMethod[]
  isDisabled?: boolean
  isUserLoggedIn: boolean
}


export const PaymentListItem = (props: PaymentListItemProps) => {
  const { paymentMethods, isDisabled, isUserLoggedIn } = props
  const { orderType, campaignCode, campaignId } = useContextCheckout()
  const { appliedPaymentMethodId, setAppliedPaymentMethodId, setRememberPaymentCardToken, setUserPaymentTokenInfo, resetUserPaymentTokenInfo, setCardSource, appliedPaymentInfo, userPaymentTokenInfo, cardSource, rememberPaymentCardToken } = useFpCampaignPaymentService({
    orderType,
    campaignCode,
    campaignId,
  })
  const handlePaymentChange = (paymentMethod: PaymentMethod) => {
    setAppliedPaymentMethodId(paymentMethod.id)
    setCardSource('')
    setRememberPaymentCardToken(false)
    resetUserPaymentTokenInfo()
  }

  const selectedPaymentMethod = useMemo(() => paymentMethods.find(method => method.id === appliedPaymentMethodId) || paymentMethods[0], [appliedPaymentMethodId, paymentMethods])
  // @ts-expect-error - TODO: fix this
  const checked = appliedPaymentMethodId === selectedPaymentMethod.id
  const paymentInfo = checked ? appliedPaymentInfo : undefined
  const { user_payment_token_infos: _userPaymentTokenInfos } = paymentInfo || {}
  const bankList = selectedPaymentMethod?.metas?.banks?.filter(bank => bank.enabled)

  const userPaymentTokenInfos = useMemo(() => {
    return _userPaymentTokenInfos?.filter((info) => {
      if (!info.expire_year || !info.expire_month) return false
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1
      const cardYear = Number(info.expire_year)
      const cardMonth = Number(info.expire_month)
      const isExpired = cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth)
      return !isExpired
    })
  }, [_userPaymentTokenInfos])

  /**
   * 因為 paymentMethods 已經是 group by type 的結果，所以只要檢查第一個元素的 type 即可
   * 此參數不能直接放 isVisible 原因是會有很多重複的 mount 導致 SDK 不能拿到正確的元件
  */
  const isCreditCard = [
    PaymentMethodType.CREDIT_CARD_INSTALLMENT,
    PaymentMethodType.CREDIT_CARD_ONCE,
    // @ts-expect-error - TODO: fix this
  ].includes(paymentMethods[0].type)
  const { t } = useTranslation()
  const translateText: Record<string, string> = useMemo(() => ({
    creditCardNumber: t('payment.creditCardNumber', { defaultValue: '信用卡號' }),
    creditCardExpiration: t('payment.creditCardExpiration', { defaultValue: '月份/年份' }),
    creditCardCode: t('payment.creditCardCode', { defaultValue: '安全碼' }),
    rememberCard: t('payment.rememberCard', { defaultValue: '記住卡片' }),
    selectCardNumber: t('payment.selectCardNumber', { defaultValue: '選擇卡號' }),
    addNewCard: t('payment.addNewCard', { defaultValue: '新增卡號' }),
    exp: t('payment.exp', { defaultValue: '效期至' }),
    pleaseSelect: t('payment.pleaseSelect', { defaultValue: '請選擇' }),
    cardholderName: t('payment.cardholderName', { defaultValue: '持卡人姓名' }),
    cardholderEmail: t('payment.cardholderEmail', { defaultValue: '持卡人 Email' }),
    cardholderPhone: t('payment.cardholderPhone', { defaultValue: '持卡人手機號碼' }),
    cardholderEmailOrPhoneRequired: `* ${t('payment.emailOrPhoneRequired', { defaultValue: 'Email 或手機號碼須擇一填寫' })}`,
  }), [t])

  const handleRememberPaymentCardTokenChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRememberPaymentCardToken(e.target.checked)
  }

  const handleChangeCardSource = (value: SelectOptionValue) => {
    setCardSource(value as string)
    if (value === CARD_SOURCE.ADD_CARD) {
      resetUserPaymentTokenInfo()
    }
    else {
      setRememberPaymentCardToken(false)
    }
  }

  const handleSelectUserPaymentTokenInfo = (newValue: SelectOptionValue) => {
    const selectedInfo = userPaymentTokenInfos?.find(info => info.payment_token_id === newValue)
    if (selectedInfo) {
      setUserPaymentTokenInfo(selectedInfo, isCreditCard)
    }
  }
  /**
   * 初始化 cardSource 行為情境如下
   * 1. 不管如何，如果 userPaymentTokenInfos.length 為 0 時，預設一定是 ADD_CARD
   * 2. 使用者一開始還未選擇 cardSource=null 時透過 userPaymentTokenInfos.length 判斷，如果有值改為 REMEMBERED，否則維持原本的 cardSource
   * */
  useEffect(() => {
    if (!isCreditCard || userPaymentTokenInfos === undefined) return
    const resetCardSource = () => {
      if (userPaymentTokenInfos.length === 0) {
        setCardSource(CARD_SOURCE.ADD_CARD)
      }
      if (!cardSource && userPaymentTokenInfos.length > 0) {
        setCardSource(CARD_SOURCE.REMEMBERED)
      }
    }
    resetCardSource()
  }, [setCardSource, cardSource, isCreditCard, userPaymentTokenInfos])

  useEffect(() => {
    const defaultToken = userPaymentTokenInfos?.[0]
    if (!userPaymentTokenInfo && cardSource === CARD_SOURCE.REMEMBERED && defaultToken) {
      setUserPaymentTokenInfo(defaultToken, isCreditCard)
    }
  }, [cardSource, userPaymentTokenInfo, userPaymentTokenInfos, setUserPaymentTokenInfo, isCreditCard])

  if (!selectedPaymentMethod) return null
  return (
    <RadioPayment
      isDisabled={isDisabled}
      xStyles={{ checked: styles.radioPaymentChecked }}
      name="checkoutPaymentMethods"
      title={PaymentMethodTitleMap[selectedPaymentMethod.type] || selectedPaymentMethod?.name}
      description={selectedPaymentMethod?.description}
      startIcon={IconMap[selectedPaymentMethod.type]}
      paymentMethods={paymentMethods}
      endIcon={isCreditCard ? <CreditCardOrganizationIcons /> : undefined}
      selectedPaymentMethodId={selectedPaymentMethod.id}
      checked={checked}
      onPaymentMethodChange={handlePaymentChange}
    >
      {isCreditCard && selectedPaymentMethod.service_type === PaymentServiceType.TAPPAY && (
        <Box rootStyle={checked ? styles.creditCardBox : undefined}>
          <TappayCreditCardFields
            isVisible={checked}
            canRememberPaymentCardToken={isUserLoggedIn}
            translateText={translateText}
            onRememberPaymentCardTokenChange={isUserLoggedIn ? handleRememberPaymentCardTokenChange : undefined}
            rememberPaymentCardTokenChecked={rememberPaymentCardToken}
            userPaymentTokenInfos={userPaymentTokenInfos}
            onChangeCardSource={handleChangeCardSource}
            selectedUserPaymentTokenInfo={userPaymentTokenInfo?.payment_token_id}
            onChangeSelectUserPaymentTokenInfo={handleSelectUserPaymentTokenInfo}
            cardSource={cardSource}
          />
        </Box>
      )}
      {isCreditCard && selectedPaymentMethod.service_type === PaymentServiceType.ECPAY && (
        <Box rootStyle={checked ? styles.creditCardBox : undefined}>
          <ECPayCreditCardFields isVisible={checked} />
        </Box>
      )}
      {bankList && (
        <Box rootStyle={styles.selectWrapper}>
          <Select fullWidth>
            {bankList.map(bank => (
              <SelectOption
                key={bank.bank_code}
                value={bank.bank_code}
              >
                {bank.name}
              </SelectOption>
            ))}
          </Select>
        </Box>
      )}
    </RadioPayment>
  )
}
